import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  publishedTime,
  modifiedTime,
}: SEOHeadProps) {
  const location = useLocation();
  const baseUrl = typeof window !== 'undefined' ? 'http://netkqn.com' : '';
  const fullCanonical = canonicalUrl ? `${baseUrl}${canonicalUrl}` : `${baseUrl}${location.pathname}`;

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);

    const linkEl = document.querySelector('link[rel="canonical"]');
    if (linkEl) {
      linkEl.setAttribute('href', fullCanonical);
    } else {
      const newLink = document.createElement('link');
      newLink.setAttribute('rel', 'canonical');
      newLink.setAttribute('href', fullCanonical);
      document.head.appendChild(newLink);
    }

    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', fullCanonical, true);
    setMeta('og:site_name', '韩漫漫画', true);
    setMeta('og:locale', 'zh_CN', true);
    if (ogImage) setMeta('og:image', ogImage, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (ogImage) setMeta('twitter:image', ogImage);

    if (publishedTime) setMeta('article:published_time', publishedTime);
    if (modifiedTime) setMeta('article:modified_time', modifiedTime);

    const metaGeoPosition = document.querySelector('meta[name="geo.position"]');
    if (!metaGeoPosition) {
      const geo = document.createElement('meta');
      geo.setAttribute('name', 'geo.position');
      geo.setAttribute('content', '37.5665;126.9780');
      document.head.appendChild(geo);
    }
    const metaGeoRegion = document.querySelector('meta[name="geo.region"]');
    if (!metaGeoRegion) {
      const region = document.createElement('meta');
      region.setAttribute('name', 'geo.region');
      region.setAttribute('content', 'KR-11');
      document.head.appendChild(region);
    }
    const metaGeoPlace = document.querySelector('meta[name="geo.placename"]');
    if (!metaGeoPlace) {
      const place = document.createElement('meta');
      place.setAttribute('name', 'geo.placename');
      place.setAttribute('content', 'Seoul, South Korea');
      document.head.appendChild(place);
    }

    const existingH1 = document.querySelector('h1');
    if (!existingH1) {
      const h1 = document.createElement('h1');
      h1.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;font-size:1px;';
      h1.textContent = title;
      document.body.appendChild(h1);
    }

    return () => {
      // Clean up dynamic h1 if needed
    };
  }, [title, description, keywords, fullCanonical, ogImage, ogType, publishedTime, modifiedTime]);

  return null;
}